import { visibleCategories, type Tool } from "@/lib/tools-data";
import { PROFESSIONAL_TRIAL_LIMIT, PREMIUM_TRIAL_LIMIT } from "@shared/tiers";
import generatedToolContent from "@/lib/generated-tool-content.json";

/**
 * The CORE knowledge base -- tools and a small hand-written FAQ set.
 * Deliberately excludes personas and blog posts (see
 * knowledgeBaseGuides.ts) -- those are meaningfully heavier (68 full
 * blog post modules, even capped to a short excerpt each at runtime,
 * still pull the entire blog-data.ts module including every post's
 * full content into whatever bundle imports it) and, more importantly,
 * are rarely what someone typing "merge pdf" or "remove background"
 * actually wants. Splitting them out means the common case -- searching
 * for a tool -- never has to load that weight at all; it's lazy-loaded
 * separately, only for queries that actually look like they might want
 * a guide/article (see knowledgeBaseSearch.ts's isQuestionLike check).
 *
 * Built once from data already in memory -- tools-data.ts, plus
 * generated-tool-content.json (auto-extracted from every tool's own
 * source file at build time -- see scripts/generate-tool-content.mjs).
 * No extra network fetch, no separate file to keep in sync by hand:
 * adding tool #205 next month means this just picks it up
 * automatically the next time the site builds.
 *
 * Multi-step requests are handled entirely by pipelineParser.ts against
 * the live query, not indexed here -- there used to be a separate set
 * of curated "workflow" docs/pages for this, removed since they were
 * static and low-value next to the dynamic detection.
 */

export type KnowledgeBaseDocType = "tool" | "faq" | "persona" | "blog";

interface GeneratedToolContent {
  what: string | null;
  how: string | null;
  tip: string | null;
}
const TOOL_CONTENT: Record<string, GeneratedToolContent> = generatedToolContent;

export interface KnowledgeBaseDoc {
  id: string;
  docType: KnowledgeBaseDocType;
  title: string;
  description: string;
  /** Extra searchable text -- category name, hand-written phrasings
   * people would actually type. Not shown in the UI, just improves
   * match quality. */
  keywords: string;
  /** Where clicking this result should go. */
  href: string;
}

// A small, honest FAQ set -- not exhaustive, just the handful of
// questions people genuinely ask before trusting a free tool site.
const FAQ_DOCS: KnowledgeBaseDoc[] = [
  {
    id: "faq-data-privacy",
    docType: "faq",
    title: "Is my file uploaded anywhere?",
    description:
      "Most tools on FreeDailyPro process your file entirely in your browser — it never leaves your device or touches a server. A handful of tools that need real docx/PDF parsing work the same way, using libraries that run locally in your browser.",
    keywords: "privacy upload server data secure safe private files stored",
    href: "/privacy",
  },
  {
    id: "faq-free",
    docType: "faq",
    title: "Can I try it before paying?",
    description:
      `Yes — no signup needed for any tool, including file-based ones. Professional Tools have ${PROFESSIONAL_TRIAL_LIMIT} trial uses per day and Premium Applications have ${PREMIUM_TRIAL_LIMIT}, rather than a hard paywall; you'll only be asked to sign in once you've used up today's trial uses and want to keep going. A $1 Day Pass or $4.99 monthly plan gives unlimited use (Premium Applications: $3 Day Pass or $9.99 monthly).`,
    keywords: "free cost price paid signup account limit pro premium",
    href: "/about",
  },
  {
    id: "faq-ai-blocked",
    docType: "faq",
    title: "Does this work if my company blocks AI tools?",
    description:
      "Yes — the core tools (PDF, image, text, calculators) are regular software, not AI, so they were never subject to an AI usage policy in the first place. No account needed for most of them, and your files are never sent to any model. The one exception is search: for complex or advanced searches the search box uses an AI language model to improve results (your files are never part of it), and if AI services are blocked on your network it falls back to standard results.",
    keywords: "corporate firewall blocked ai policy work company restricted chatgpt claude",
    href: "/corporate-safe-tools",
  },
  {
    id: "faq-account",
    docType: "faq",
    title: "Do I need to sign in?",
    description:
      "No — every tool, including file-based ones, works with no account at all up to the daily trial limit. You'll only be asked to sign in once you've used that up and want a plan for unlimited access. One note: your daily trial uses are tracked per browser, so private/incognito windows or a VPN that keeps changing your connection may not track your usage as expected — for the smoothest experience, we'd recommend a regular browser window.",
    keywords: "sign in login account required signup register incognito private vpn",
    href: "/about",
  },
];

function toolToDoc(tool: Tool, categoryName: string): KnowledgeBaseDoc {
  // Real what/how/tip copy, auto-extracted from this tool's own file at
  // build time -- adds real phrasing (symptoms, use cases, the exact
  // words someone experiencing a problem would type) well beyond the
  // one-line catalog description alone, at zero ongoing maintenance
  // cost since it's regenerated fresh every build, never hand-copied.
  const generated = TOOL_CONTENT[tool.id];
  const extraContent = generated
    ? [generated.what, generated.how, generated.tip].filter(Boolean).join(" ")
    : "";

  return {
    id: `tool-${tool.id}`,
    docType: "tool",
    title: tool.name,
    description: tool.description,
    keywords: `${categoryName} ${tool.region ? `${tool.region} specific` : ""} ${extraContent}`.trim(),
    href: `/tool/${tool.id}`,
  };
}

let cachedCoreDocs: KnowledgeBaseDoc[] | null = null;

export function getCoreKnowledgeBaseDocs(): KnowledgeBaseDoc[] {
  if (cachedCoreDocs) return cachedCoreDocs;

  const toolDocs = visibleCategories.flatMap((category) =>
    category.tools.map((tool) => toolToDoc(tool, category.name))
  );

  cachedCoreDocs = [...toolDocs, ...FAQ_DOCS];
  return cachedCoreDocs;
}
